/* eslint-disable max-lines */
import type { IRiskChangeField, IRiskStatus, IRiskTreatment } from '@/types/risk'
import { RISK_LEVELS as RISK_LEVELS_CONSTANTS } from '@/lib/constants/risks'
export const RISK_TABLE = {
  TITLE: 'Risk Manager',
  CREATE: 'New Risk',
  SEARCH_PLACEHOLDER: 'Search risks...',
  REVIEWED_ON: (date: string) => `Reviewed ${date}`,
  BULK_UPLOAD: 'Import CSV',
  EXPORT: {
    BUTTON: 'Export CSV',
    EMPTY: 'There are no risks to export with the current filters.',
  },
  METHODOLOGY: {
    LABEL: 'Methodology',
    SIMPLE: 'Simple',
    ADVANCED: 'Advanced',
    ADVANCED_LOCKED: 'Upgrade to unlock the advanced risk manager',
    BANNER_DESCRIPTION:
      'The Advanced methodology adds the ISO 27005 layer to every risk: ready-made risks from the catalogue, and suggested threats and vulnerabilities.',
    SWITCH_TO_ADVANCED: 'Switch to Advanced',
    REQUEST_UPGRADE: 'Request upgrade',
    UPGRADE_EMAIL_SUBJECT: 'Advanced risk manager',
  },
}
export const RISK_BULK_UPLOAD_DIALOG = {
  TITLE: 'Import Risks from CSV',
  DESCRIPTION:
    'Upload a CSV file to import risks in bulk. All rows must be valid — any errors will be shown below and nothing will be created until you fix them.',
  UPLOAD_FILE: 'Upload CSV',
  UPLOADING: 'Uploading...',
  CREATING: 'Creating risks...',
  SUCCESS: 'Risks imported successfully',
  CANCEL: 'Cancel',
  VALIDATION_TABLE_ROW: 'Row',
  VALIDATION_TABLE_FIELD: 'Field',
  VALIDATION_TABLE_ERROR: 'Error',
  VALIDATION_TABLE_RECEIVED_VALUE: 'Received value',
  DOWNLOAD_SAMPLE: 'Download sample CSV',
  HEADER_ERROR_TITLE: 'Your CSV header has problems',
  HEADER_ERROR_DESCRIPTION: 'Fix the header row in your file and re-upload. The accepted columns are listed below.',
  ACCEPTED_COLUMNS_REQUIRED: 'Required columns',
  ACCEPTED_COLUMNS_OPTIONAL: 'Optional columns',
  SUMMARY_LINE: '{totalRows} rows checked, {errorCount} errors found. Fix them in your CSV and re-upload.',
  ERROR_UNKNOWN_COLUMN: 'Unknown column: {field}',
  ERROR_MISSING_REQUIRED_COLUMN: 'Required column missing: {field}',
  ERROR_MALFORMED_ROW: 'Malformed row',
  ERROR_REQUIRED: '{field} is required',
  ERROR_INVALID_INTEGER_RANGE: '{field} must be an integer in the allowed range. Got "{receivedValue}"',
  ERROR_INVALID_DATE: 'Invalid date in {field}. Got "{receivedValue}"',
  ERROR_INVALID_TREATMENT_TYPE: 'Invalid treatment type "{receivedValue}". Allowed values: {allowedValues}',
  ERROR_INVALID_CLUSTER: 'Invalid cluster "{receivedValue}". Allowed values: {allowedValues}',
  ERROR_INVALID_ASSET: 'Invalid asset "{receivedValue}". Allowed values: {allowedValues}',
  ERROR_INVALID_INTERESTED_PARTY: 'Invalid interested party "{receivedValue}". Allowed values: {allowedValues}',
  ERROR_OWNER_NOT_FOUND: 'User not found in this organization: "{receivedValue}"',
  ERROR_DUPLICATE_IN_FILE: 'Duplicate name in this file: "{receivedValue}"',
  ERROR_DUPLICATE_IN_DB: 'A risk with this name already exists: "{receivedValue}"',
  ERROR_TREATMENT_CONTROL_NOT_FOUND: 'Treatment control not found: "{receivedValue}"',
  ERROR_INVALID_STATUS: 'Invalid status "{receivedValue}". Allowed values: {allowedValues}',
  ERROR_INVALID_THREAT: 'Invalid threat "{receivedValue}". Allowed values: {allowedValues}',
  ERROR_INVALID_VULNERABILITY: 'Invalid vulnerability "{receivedValue}". Allowed values: {allowedValues}',
  ERROR_INVALID_IMPACT_DIMENSION: 'Invalid impact dimension "{receivedValue}". Allowed values: {allowedValues}',
  ERROR_AFFECTED_ASSET_NOT_FOUND: 'Asset not found in your inventory: "{receivedValue}"',
  ERROR_OPERATIONAL_ERROR: 'Unable to create risks right now. Please try again.',
}
export const RISK_FILTERS = {
  ALL: 'All',
  FILTER_BY_OWNER: 'Filter by owner',
  FILTER_BY_CLUSTER: 'Filter by risk domain',
  FILTER_BY_ASSET: 'Filter by asset',
  FILTER_BY_INTERESTED_PARTY: 'Filter by interested party',
  FILTER_BY_TREATMENT: 'Filter by treatment',
  FILTER_BY_CONTROL: 'Filter by safeguard',
  FILTER_BY_INHERENT_RISK: 'Filter by inherent risk',
  FILTER_BY_RESIDUAL_RISK: 'Filter by residual risk',
}

export const RISK_BULK_ACTIONS = {
  SELECTED: 'selected',
  UPDATING: 'Updating...',
  CLEAR: 'Clear',
  DELETE: 'Delete',
  CANCEL: 'Cancel',
  CONFIRM_DELETE_TITLE: 'Delete risks',
  CONFIRM_DELETE_DESCRIPTION: (count: number) =>
    `Are you sure you want to delete ${count} ${count === 1 ? 'risk' : 'risks'}? This action cannot be undone.`,
}

export const RISK_DRAWER = {
  RISK_DETAILS: 'Risk Details',
  ATTRIBUTES: 'Attributes',
  RISK: 'Risk',
  NOTES: 'Notes',
  NO_NOTES: 'No notes',
  THREATS_AND_VULNERABILITIES: 'Threats and vulnerabilities',
  CREATED: 'Created',
  CLOSED: 'Closed',
  NOT_SET: 'Not set',
  RISK_DOMAIN: 'Risk domain',
  NO_RISK_DOMAIN: 'No risk domain',
  INHERENT_RISK: 'Inherent risk',
  INHERENT_RISK_INFO: 'Before any measure is applied',
  TREATMENT: 'Treatment',
  SAFEGUARDS: 'Safeguards',
  NO_SAFEGUARD: 'No safeguard',
  SELECT_SAFEGUARDS: 'Select safeguards...',
  EFFECTIVENESS: 'Effectiveness',
  RESIDUAL_RISK: 'Residual risk',
  RESIDUAL_RISK_INFO: 'What remains after the measures above',
  REACH: 'Reach',
  REACH_PLACEHOLDER: 'Who or what this risk could affect...',
  REACH_INFO: 'Processes, sites and systems it extends to',
}

export const RISK_HISTORY = {
  TITLE: 'Change history',
  CREATED: 'Risk registered',
  NO_CHANGES: 'No changes yet',
  EMPTY_VALUE: 'None',
  UNKNOWN_USER: 'System',
  FETCH_ERROR: 'Could not load the change history',
  FIELDS: {
    displayId: 'Risk ID',
    name: 'Name',
    status: 'Status',
    owner: 'Owner',
    inherentLikelihood: 'Inherent likelihood',
    inherentImpact: 'Inherent consequence',
    inherentRiskLevel: 'Inherent risk',
    residualLikelihood: 'Residual likelihood',
    residualImpact: 'Residual consequence',
    residualRiskLevel: 'Residual risk',
    effectiveness: 'Effectiveness',
    treatmentType: 'Treatment',
    treatmentNotes: 'Measures',
    controls: 'Safeguards',
    clusters: 'Risk domain',
    assets: 'Assets',
    affectedAssets: 'Affected assets',
    threats: 'Threats',
    vulnerabilities: 'Vulnerabilities',
    impactDimensions: 'Impact dimensions',
    interestedParties: 'Interested parties',
    reach: 'Reach',
    notes: 'Notes',
    createdAt: 'Created date',
    closedDate: 'Closed date',
  } satisfies { [K in IRiskChangeField]: string },
}

export const RISK_CREATION_DIALOG = {
  CREATE: 'Create Risk',
  NAME: 'Name',
  NOTES: 'Notes',
  NO_NOTES: 'No notes',
  CATEGORIES: 'Categories',
  BULK_UPLOAD: 'Bulk Upload',
  BULK_UPLOAD_TEMPLATE: 'Bulk Upload Template',
}

export const RISK_DETAILS = {
  DESCRIPTION: 'Description',
  OWNER: 'Owner',
  STATUS: 'Status',
  TREATMENT: 'Treatment',
  INHERENT: 'Inherent Risk',
  RESIDUAL: 'Residual Risk',
  RELATED_CONTROLS: 'Related Controls',
  MISSING_RISK_DESCRIPTION: 'Missing risk description',
  MISSING_NOTES: 'Missing notes',
  LIKELIHOOD: 'Likelihood',
  IMPACT: 'Consequence',
  SEVERITY: 'Severity',
  NOTES: 'Notes',
  CATEGORIES: 'Categories',
  TREATMENT_NOTES: 'Potential Measure',
  TREATMENT_CONTROLS: 'Associated Controls',
  TREATMENT_TYPE: 'Type',
  CREATED_AT: 'Created At',
  EFFECTIVENESS: 'Effectiveness',
  DELETED: 'Risk deleted',
}
export const RISK_DELETE_DIALOG = {
  TITLE: 'Delete Risk',
  QUESTION: 'Are you sure you want to delete this risk?',
}

export const RISK_STATUS: { [K in IRiskStatus]: string } = {
  inProgress: 'In Progress',
  planned: 'Planned',
  delayed: 'Delayed',
  closed: 'Closed',
}

export const RISK_TREATMENT_LABELS: { [K in IRiskTreatment['type']]: string } = {
  mitigate: 'Mitigate',
  accept: 'Accept',
}

export const RISK_TREATMENT: { [K in IRiskTreatment['type']]: string } = {
  mitigate: 'Mitigate',
  accept: 'Accept',
}

export const RISK_TREATMENT_DESCRIPTIONS: { [K in IRiskTreatment['type']]: string } = {
  mitigate: 'Identify controls to put in place or tasks to be done that will reduce the risk score.',
  accept: 'Decide to live with the risk and take no further actions.',
}

export const RISK_ASSESSMENT = {
  TITLE: 'Risk Assessment',
  ADD_CONTROL: 'Add Control',
  CONTROL_TABLE_HEADER: 'Control',
  UPDATING_RISK: 'Updating risk...',
  UPDATED_RISK: 'Risk updated',
  LAST_REVIEW_DATE: 'Last Review Date',
}

export const RISK_ASSESSMENT_TABLE = {
  TABLE_HEADER: {
    NAME: 'Name',
    DESCRIPTION: 'Description',
    OWNER: 'Owner',
    STATUS: 'Status',
    TREATMENT: 'Treatment',
    INHERENT_RISK: 'Inherent Risk',
  },
}

export const RISK_ASSESSMENT_STATUS = {
  planned: 'Planned',
  delayed: 'Delayed',
  closed: 'Closed',
  inProgress: 'In Progress',
}

export const RISK_LEVELS = {
  [RISK_LEVELS_CONSTANTS.VERY_LOW]: 'Very Low',
  [RISK_LEVELS_CONSTANTS.LOW]: 'Low',
  [RISK_LEVELS_CONSTANTS.MEDIUM]: 'Medium',
  [RISK_LEVELS_CONSTANTS.HIGH]: 'High',
  [RISK_LEVELS_CONSTANTS.VERY_HIGH]: 'Very High',
  [RISK_LEVELS_CONSTANTS.UNKNOWN]: 'Unknown',
}
export const RISK_CONTROLS_TABLE = {
  CONTROLS: 'Controls',
  STANDARDS: 'Standards',
  ACTIONS: 'Actions',
}

export const RISK_LEVEL_SELECTOR = {
  LIKELIHOOD: 'Likelihood',
  IMPACT: 'Impact',
  APPLY: 'Apply',
  RISK_LEVEL: 'Risk Level',
}

export const RISK_FORM = {
  MEASURES: 'Measures',
  MEASURES_PLACEHOLDER: 'Describe the measures to mitigate this risk...',
  CONTROLS: 'Safeguards',
  EFFECTIVENESS: 'Effectiveness',
  CANCEL: 'Cancel',
  DELETE: 'Delete',
}

export const RISK_MESSAGES = {
  CREATING: 'Creating risk...',
  CREATED: (name: string) => `Risk "${name}" created`,
  DELETED: 'Risk deleted',
  BULK_EDIT_OWNER_FAILED: 'Failed to bulk edit owner',
  BULK_EDIT_OWNER_SUCCESS: (count: number) =>
    `Successfully updated owner for ${count} ${count === 1 ? 'risk' : 'risks'}`,
  BULK_EDIT_STATUS_FAILED: 'Failed to bulk edit status',
  BULK_EDIT_STATUS_SUCCESS: (count: number) =>
    `Successfully updated status for ${count} ${count === 1 ? 'risk' : 'risks'}`,
  BULK_DELETE_FAILED: 'Failed to delete risks',
  BULK_DELETE_SUCCESS: (count: number) => `${count} ${count === 1 ? 'risk' : 'risks'} deleted`,
}

export const RISK_ASSETS = {
  servers_it_production: 'IT Servers - Production',
  servers_internal_development: 'Internal Servers - Development',
  source_code: 'Source Code',
  web_applications_external_production: 'Web Applications External Production',
  physical_servers: 'Physical Servers',
  laptops_windows_linux_mac: 'Laptops Windows, Linux, Mac',
  byod: 'BYOD',
  corporate_mobiles: 'Corporate Mobiles',
  corporate_tablets: 'Corporate Tablets',
  usb_drives: 'USB Drives',
  user_peripherals: 'User Peripherals',
  printers: 'Printers',
  offices_barcelona: 'Offices Barcelona',
  wireless_infrastructure: 'Wireless Infrastructure',
  subcontracted_office_facilities: 'Subcontracted Office Facilities',
  company_owned_office_facilities: 'Company-Owned Office Facilities',
  saas_core: 'SaaS Core',
  saas_non_core: 'SaaS Non Core',
  public_information: 'Public Information',
  confidential_information_technical_organizational: 'Confidential Information (Technical/Organizational)',
  confidential_information_personal_pii_phi: 'Confidential Information (Personal - PII, PHI)',
  confidential_information_core: 'Confidential Information (Core)',
  top_management: 'Top Management',
  employees: 'Employees',
  freelancers: 'Freelancers',
  administrators: 'Administrators',
}

export const NO_ASSET = 'No Asset'

export const ASSET_SELECTOR = {
  SEARCH_ASSETS: 'Search assets...',
  NO_ASSET_FOUND: 'No asset found.',
}

export const AFFECTED_ASSETS_SELECTOR = {
  LABEL: 'Affected assets',
  NO_ASSET: 'No asset',
  SEARCH: 'Search assets',
  NO_RESULTS: 'No assets found',
  LOAD_ERROR: 'Assets could not be loaded',
  INVENTORY_COUNT_ONE: '1 asset in the inventory',
  INVENTORY_COUNT: '{count} assets in the inventory',
  MANAGE: 'Manage in Asset Inventory',
}

export const RISK_INTERESTED_PARTIES = {
  employees: 'Employees',
  systems_administrators: 'Systems Administrators',
  freelancers: 'Freelancers',
  clients: 'Clients',
  prospects: 'Prospects',
  suppliers: 'Suppliers',
  shareholders: 'Shareholders',
  regulatory_authorities: 'Regulatory Authorities',
  government: 'Government',
  government_agencies_cybersecurity: 'Government Agencies (Cybersecurity)',
  society: 'Society',
  competitors: 'Competitors',
  business_partners: 'Business Partners',
}

export const NO_INTERESTED_PARTY = 'No Interested Party'

export const INTERESTED_PARTY_SELECTOR = {
  SEARCH_INTERESTED_PARTIES: 'Search interested parties...',
  NO_INTERESTED_PARTY_FOUND: 'No interested party found.',
  ADD_PLACEHOLDER: 'Add an interested party',
  ADD: 'Add',
}

export const NO_CONTROL = 'No Control'

export const RISK_DASHBOARD = {
  TABS: {
    OVERVIEW: 'Overview',
    RISK_LIST: 'Risk List',
    TREATMENT_PLANS: 'Treatment plans',
  },
  SUMMARY: {
    TOTAL_RISKS: 'Identified Risks',
    HIGH_SEVERITY: 'High Severity Risks',
    TREATMENT: 'Treatment Foreseen',
    MITIGATE: 'Mitigate',
    ACCEPT: 'Accept',
  },
  HEATMAP: {
    TITLE: 'Risk Heatmap',
    LIKELIHOOD: 'Likelihood',
    IMPACT: 'Impact',
    VIEW_ALL: 'View All Risks →',
    INHERENT_RISK: 'Inherent Risk',
    RESIDUAL_RISK: 'Residual Risk',
    TOOLTIP: {
      RISK: 'Risk',
      RISKS: 'Risks',
      AT: 'at',
      MORE: 'more...',
    },
  },
  TOP_CLUSTERS: {
    TITLE: 'Top Affected Clusters',
    NO_DATA: 'No cluster data available',
  },
  EVOLUTION: {
    TITLE: 'Risk evolution',
    DESCRIPTION: 'Residual risk at each review',
    CURRENT: 'Current',
    SINCE: 'Since',
    NO_EARLIER_REVIEW: 'No earlier review',
    REVIEW: 'Review',
    NEW_RISKS: 'New risks',
    MORE: 'more',
  },
  CATALOGUE: {
    TOP_THREATS: 'Top threats',
    TOP_VULNERABILITIES: 'Top vulnerabilities',
  },
}
